import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaTimes, FaChevronLeft, FaChevronRight, FaCheck, FaImage } from 'react-icons/fa';
import {
  WIZARD_MODES,
  UNIT_TYPES,
  UNIT_STATUS_OPTIONS,
  SAUDI_CITIES,
  getGroupedFieldsForType,
  getUnitTypeLabel,
  generateUnitNumbers,
  buildUnitPayload,
  defaultSpecsForType,
  assignBulkStatuses,
} from '../utils/unitTypeSpecs';
import { getNeighborhoodsForCity, OTHER_NEIGHBORHOOD } from '../utils/saudiNeighborhoods';
import { resolveCoverImage } from '../utils/propertyCovers';
import SearchableNeighborhoodSelect from './SearchableNeighborhoodSelect';
import {
  fetchProjects,
  createProject,
  createProperty,
  createUnitsBulk,
  createListing,
} from '../utils/inventoryWizardApi';
import './InventoryWizard.css';

const INITIAL = {
  mode: 'single',
  city: '',
  district: '',
  districtOther: '',
  projectName: '',
  propertyName: '',
  useExistingProject: false,
  existingProjectId: '',
  unitType: 'apartment',
  coverImageUrl: '',
  specs: defaultSpecsForType('apartment'),
  availableFor: 'rent',
  monthlyRent: '',
  salePrice: '',
  status: 'vacant',
  bulkSold: 0,
  bulkRented: 0,
  bulkReserved: 0,
  unitNumber: '',
  notes: '',
  bulkCount: 2,
  bulkPrefix: '',
  bulkStart: 1,
  bulkPad: 0,
  marketing: 'later',
  listingType: 'rent',
};

function stepIdsForMode(mode) {
  const base = ['mode', 'location', 'type', 'specs'];
  if (mode === 'bulk' || mode === 'project') base.push('bulk');
  base.push('marketing', 'review');
  return base;
}

function resolvedDistrict(form) {
  if (form.district === OTHER_NEIGHBORHOOD) return (form.districtOther || '').trim();
  return (form.district || '').trim();
}

export default function InventoryWizard({ language, onClose, onSuccess, initialMode = 'single' }) {
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);
  const fileInputRef = useRef(null);

  const [stepIndex, setStepIndex] = useState(0);
  const [form, setForm] = useState({ ...INITIAL, mode: initialMode });
  const [projects, setProjects] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const steps = useMemo(() => stepIdsForMode(form.mode), [form.mode]);
  const currentStep = steps[stepIndex];
  const neighborhoodOptions = useMemo(() => getNeighborhoodsForCity(form.city), [form.city]);
  const isMulti = form.mode === 'bulk' || form.mode === 'project';
  const coverPreview = resolveCoverImage({
    coverImageUrl: form.coverImageUrl,
    unitType: form.unitType,
  });

  useEffect(() => {
    fetchProjects().then(setProjects).catch(() => setProjects([]));
  }, []);

  const patch = (updates) => setForm((prev) => ({ ...prev, ...updates }));

  const setCity = (city) => {
    setForm((prev) => {
      const nextOptions = getNeighborhoodsForCity(city);
      const keep =
        prev.district === OTHER_NEIGHBORHOOD ||
        nextOptions.some((n) => n.value === prev.district);
      return {
        ...prev,
        city,
        district: keep ? prev.district : '',
        districtOther: keep && prev.district === OTHER_NEIGHBORHOOD ? prev.districtOther : '',
      };
    });
  };

  const setUnitType = (unitType) => {
    patch({ unitType, specs: defaultSpecsForType(unitType) });
  };

  const unitPreview = useMemo(() => {
    if (form.mode === 'single') return [form.unitNumber || t('(رقم الوحدة)', '(unit #)')];
    return generateUnitNumbers({
      count: Math.min(Math.max(parseInt(form.bulkCount, 10) || 1, 1), 500),
      prefix: form.bulkPrefix,
      start: parseInt(form.bulkStart, 10) || 1,
      pad: parseInt(form.bulkPad, 10) || 0,
    });
  }, [form, isAr]);

  const bulkStatusPreview = useMemo(() => {
    if (!isMulti) return null;
    const count = Math.min(Math.max(parseInt(form.bulkCount, 10) || 1, 1), 500);
    const statuses = assignBulkStatuses(count, {
      sold: form.bulkSold,
      rented: form.bulkRented,
      reserved: form.bulkReserved,
    });
    const vacant = statuses.filter((s) => s === 'vacant').length;
    return {
      count,
      vacant,
      sold: statuses.filter((s) => s === 'sold').length,
      rented: statuses.filter((s) => s === 'rented').length,
      reserved: statuses.filter((s) => s === 'reserved').length,
    };
  }, [isMulti, form.bulkCount, form.bulkSold, form.bulkRented, form.bulkReserved]);

  const validateStep = () => {
    setError('');
    if (currentStep === 'location') {
      if (!form.city.trim()) return t('اختر المدينة', 'Select city');
      const district = resolvedDistrict(form);
      if (!district) {
        return form.district === OTHER_NEIGHBORHOOD
          ? t('اكتب اسم الحي', 'Enter neighborhood name')
          : t('اختر الحي', 'Select neighborhood');
      }
      if (form.mode === 'project' && !form.useExistingProject && !form.projectName.trim()) {
        return t('اسم المشروع مطلوب', 'Project name is required');
      }
      if (form.useExistingProject && !form.existingProjectId) {
        return t('اختر مشروعاً موجوداً', 'Select an existing project');
      }
    }
    if (currentStep === 'specs') {
      if (form.mode === 'single' && !form.unitNumber.trim()) {
        return t('رقم الوحدة مطلوب', 'Unit number is required');
      }
    }
    if (currentStep === 'bulk') {
      const c = parseInt(form.bulkCount, 10);
      if (!c || c < 1) return t('أدخل عدداً صحيحاً', 'Enter a valid count');
      const sold = parseInt(form.bulkSold, 10) || 0;
      const rented = parseInt(form.bulkRented, 10) || 0;
      const reserved = parseInt(form.bulkReserved, 10) || 0;
      if (sold + rented + reserved > c) {
        return t(
          'مجموع المباعة والمؤجرة والمحجوزة أكبر من عدد الوحدات',
          'Sold + rented + reserved exceeds unit count'
        );
      }
    }
    return null;
  };

  const goNext = () => {
    const err = validateStep();
    if (err) {
      setError(err);
      return;
    }
    setStepIndex((i) => Math.min(i + 1, steps.length - 1));
  };

  const goBack = () => {
    setError('');
    setStepIndex((i) => Math.max(i - 1, 0));
  };

  const propertyTypeFromUnit = (unitType) => {
    if (['shop', 'showroom', 'office', 'warehouse'].includes(unitType)) return 'commercial';
    if (unitType === 'land') return 'land';
    return 'residential';
  };

  const onCoverFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError(t('اختر ملف صورة', 'Please choose an image file'));
      return;
    }
    if (file.size > 2_500_000) {
      setError(t('الصورة كبيرة جداً (حد أقصى ٢٫٥ ميجا)', 'Image too large (max 2.5MB)'));
      return;
    }
    setError('');
    const reader = new FileReader();
    reader.onload = () => patch({ coverImageUrl: String(reader.result || '') });
    reader.onerror = () => setError(t('تعذر قراءة الصورة', 'Could not read image'));
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');
    try {
      const district = resolvedDistrict(form);
      let projectId = form.useExistingProject ? parseInt(form.existingProjectId, 10) : null;
      let propertyId = null;
      const coverToSave = form.coverImageUrl || null;

      if (form.mode === 'project' && !projectId) {
        const proj = await createProject({
          name: form.projectName.trim(),
          city: form.city,
          district: district || null,
          project_type: propertyTypeFromUnit(form.unitType),
          cover_image_url: coverToSave,
          metadata: { primary_unit_type: form.unitType },
        });
        projectId = proj.id;
      } else if (form.mode === 'bulk' && form.projectName.trim()) {
        const proj = await createProject({
          name: form.projectName.trim(),
          city: form.city,
          district: district || null,
          project_type: propertyTypeFromUnit(form.unitType),
          cover_image_url: coverToSave,
          metadata: { primary_unit_type: form.unitType },
        });
        projectId = proj.id;
      }

      const shouldCreateProperty =
        Boolean(form.propertyName.trim()) ||
        form.mode === 'project' ||
        (form.mode === 'bulk' && (projectId || form.projectName.trim()));

      if (shouldCreateProperty) {
        const prop = await createProperty({
          name:
            form.propertyName.trim() ||
            form.projectName.trim() ||
            t('العقار الرئيسي', 'Main property'),
          city: form.city,
          district: district || null,
          property_type: propertyTypeFromUnit(form.unitType),
          project_id: projectId,
          cover_image_url: coverToSave,
          metadata: { primary_unit_type: form.unitType },
        });
        propertyId = prop.id;
      }

      const numbers =
        form.mode === 'single'
          ? [form.unitNumber.trim()]
          : generateUnitNumbers({
              count: form.bulkCount,
              prefix: form.bulkPrefix,
              start: parseInt(form.bulkStart, 10) || 1,
              pad: parseInt(form.bulkPad, 10) || 0,
            });

      const statusList = isMulti
        ? assignBulkStatuses(numbers.length, {
            sold: form.bulkSold,
            rented: form.bulkRented,
            reserved: form.bulkReserved,
          })
        : numbers.map(() => form.status || 'vacant');

      const units = numbers.map((num, idx) => ({
        ...buildUnitPayload(form, num, statusList[idx]),
        property_id: propertyId,
        project_id: projectId,
        cover_image_url: coverToSave,
      }));

      const bulkRes = await createUnitsBulk(units, { property_id: propertyId, project_id: projectId });
      const unitIds = bulkRes.unit_ids || [];

      const listingType = form.availableFor === 'sale' ? 'sale' : form.availableFor === 'both' ? form.listingType : 'rent';
      const price = listingType === 'sale' ? form.salePrice : form.monthlyRent;

      if (form.marketing === 'project' || form.marketing === 'both') {
        if (projectId) {
          await createListing({
            project_id: projectId,
            title: form.projectName || form.propertyName || form.unitNumber,
            listing_type: listingType,
            price: price || null,
          });
        }
      }

      if (form.marketing === 'units' || form.marketing === 'both') {
        for (let i = 0; i < unitIds.length; i += 1) {
          if (statusList[i] === 'sold' || statusList[i] === 'rented') continue;
          await createListing({
            unit_id: unitIds[i],
            listing_type: listingType,
            price: price || null,
          });
        }
      }

      onSuccess?.({
        projectId,
        propertyId,
        unitIds,
        unitCount: unitIds.length,
      });
      onClose();
    } catch (e) {
      setError(e.response?.data?.error || e.message || t('فشل الحفظ', 'Save failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const renderModeStep = () => (
    <div className="iw-cards">
      {WIZARD_MODES.map((m) => (
        <button
          key={m.id}
          type="button"
          className={`iw-card${form.mode === m.id ? ' iw-card--active' : ''}`}
          onClick={() => patch({ mode: m.id })}
        >
          <span className="iw-card__icon">{m.icon}</span>
          <span className="iw-card__title">{isAr ? m.labelAr : m.labelEn}</span>
          <span className="iw-card__desc">{isAr ? m.descAr : m.descEn}</span>
        </button>
      ))}
    </div>
  );

  const renderLocationStep = () => (
    <div className="iw-form-grid">
      <label className="iw-field iw-field--full">
        <span>{t('المدينة', 'City')} *</span>
        <select value={form.city} onChange={(e) => setCity(e.target.value)}>
          <option value="">{t('اختر المدينة', 'Select city')}</option>
          {SAUDI_CITIES.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </label>
      <label className="iw-field iw-field--full">
        <span>{t('الحي', 'Neighborhood')} *</span>
        <SearchableNeighborhoodSelect
          language={language}
          city={form.city}
          options={neighborhoodOptions}
          value={form.district}
          disabled={!form.city}
          required
          onChange={(district) =>
            patch({
              district,
              districtOther: district === OTHER_NEIGHBORHOOD ? form.districtOther : '',
            })
          }
        />
        {!form.city && (
          <p className="iw-field-hint">{t('فعّل قائمة الأحياء باختيار المدينة أولاً.', 'Pick a city first to enable neighborhoods.')}</p>
        )}
        {form.city && neighborhoodOptions.length === 0 && form.district !== OTHER_NEIGHBORHOOD && (
          <p className="iw-field-hint">{t('لا توجد قائمة أحياء لهذه المدينة — اختر «حي آخر».', 'No list for this city — choose Other.')}</p>
        )}
      </label>
      {form.district === OTHER_NEIGHBORHOOD && (
        <label className="iw-field iw-field--full">
          <span>{t('اسم الحي', 'Neighborhood name')} *</span>
          <input
            value={form.districtOther}
            onChange={(e) => patch({ districtOther: e.target.value })}
            placeholder={t('مثال: النرجس', 'e.g. Al Narjis')}
            autoFocus
          />
        </label>
      )}
      {(form.mode === 'project' || form.mode === 'bulk') && (
        <>
          {form.mode === 'project' && (
            <label className="iw-check iw-field--full">
              <input
                type="checkbox"
                checked={form.useExistingProject}
                onChange={(e) => patch({ useExistingProject: e.target.checked })}
              />
              <span>{t('ربط بمشروع موجود', 'Link to existing project')}</span>
            </label>
          )}
          {form.useExistingProject ? (
            <label className="iw-field iw-field--full">
              <span>{t('المشروع', 'Project')} *</span>
              <select
                value={form.existingProjectId}
                onChange={(e) => patch({ existingProjectId: e.target.value })}
              >
                <option value="">{t('اختر مشروعاً', 'Select project')}</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </label>
          ) : (
            <label className="iw-field iw-field--full">
              <span>
                {form.mode === 'project' ? t('اسم المشروع', 'Project name') : t('اسم المشروع (اختياري)', 'Project name (optional)')}
                {form.mode === 'project' ? ' *' : ''}
              </span>
              <input value={form.projectName} onChange={(e) => patch({ projectName: e.target.value })} />
            </label>
          )}
          <label className="iw-field iw-field--full">
            <span>{t('اسم العقار / المبنى (اختياري)', 'Property / building name (optional)')}</span>
            <input value={form.propertyName} onChange={(e) => patch({ propertyName: e.target.value })} />
          </label>
        </>
      )}
      {form.mode === 'single' && (
        <label className="iw-field iw-field--full">
          <span>{t('اسم العقار (اختياري)', 'Property name (optional)')}</span>
          <input
            value={form.propertyName}
            onChange={(e) => patch({ propertyName: e.target.value })}
            placeholder={t('اتركه فارغاً للوحدة المنفردة', 'Leave empty for standalone unit')}
          />
        </label>
      )}
    </div>
  );

  const renderTypeStep = () => (
    <div>
      <div className="iw-type-grid">
        {UNIT_TYPES.map((type) => (
          <button
            key={type.value}
            type="button"
            className={`iw-type-btn${form.unitType === type.value ? ' iw-type-btn--active' : ''}`}
            onClick={() => setUnitType(type.value)}
          >
            <span>{type.icon}</span>
            <span>{isAr ? type.labelAr : type.labelEn}</span>
          </button>
        ))}
      </div>
      <div className="iw-cover-block">
        <img className="iw-cover-preview" src={coverPreview} alt="" />
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="iw-file-input"
          onChange={onCoverFile}
        />
        <div className="iw-cover-actions">
          <button
            type="button"
            className="iw-btn iw-btn--ghost iw-btn--sm"
            onClick={() => fileInputRef.current?.click()}
          >
            <FaImage /> {t('رفع صورة', 'Upload photo')}
          </button>
          {form.coverImageUrl ? (
            <button type="button" className="iw-linkish" onClick={() => patch({ coverImageUrl: '' })}>
              {t('استخدام الصورة الافتراضية', 'Use type default')}
            </button>
          ) : (
            <span className="iw-hint-inline">
              {t('تُعرض صورة افتراضية حسب النوع حتى ترفع صورة.', 'Type default shows until you upload.')}
            </span>
          )}
        </div>
      </div>
    </div>
  );

  const renderSpecsStep = () => {
    const groups = getGroupedFieldsForType(form.unitType, form.specs);
    const setSpec = (key, value) => {
      const next = { ...form.specs, [key]: value };
      if (key === 'has_private_yard' && !value) next.yard_area_sqm = '';
      patch({ specs: next });
    };

    const renderField = (field) => {
      if (field.type === 'boolean') {
        const on = Boolean(form.specs[field.key]);
        return (
          <button
            key={field.key}
            type="button"
            className={`iw-chip${on ? ' iw-chip--on' : ''}`}
            aria-pressed={on}
            onClick={() => setSpec(field.key, !on)}
          >
            {isAr ? field.labelAr : field.labelEn}
          </button>
        );
      }
      return (
        <label key={field.key} className="iw-field">
          <span>{isAr ? field.labelAr : field.labelEn}</span>
          <input
            type={field.type === 'number' ? 'number' : 'text'}
            min={field.min}
            max={field.max}
            step={field.step}
            value={form.specs[field.key] ?? ''}
            onChange={(e) => setSpec(field.key, e.target.value)}
          />
        </label>
      );
    };

    return (
      <div className="iw-form-grid">
        {form.mode === 'single' && (
          <label className="iw-field iw-field--full">
            <span>{t('رقم / معرف الوحدة', 'Unit number / ID')} *</span>
            <input
              value={form.unitNumber}
              onChange={(e) => patch({ unitNumber: e.target.value })}
              placeholder={t('مثال: A101 أو فيلا 3', 'e.g. A101 or Villa 3')}
            />
          </label>
        )}
        <label className="iw-field">
          <span>{t('الغرض', 'Purpose')}</span>
          <select value={form.availableFor} onChange={(e) => patch({ availableFor: e.target.value })}>
            <option value="rent">{t('إيجار', 'Rent')}</option>
            <option value="sale">{t('بيع', 'Sale')}</option>
            <option value="both">{t('بيع وإيجار', 'Sale & rent')}</option>
          </select>
        </label>
        {!isMulti && (
          <label className="iw-field">
            <span>{t('حالة الوحدة', 'Unit status')}</span>
            <select value={form.status} onChange={(e) => patch({ status: e.target.value })}>
              {UNIT_STATUS_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>
                  {isAr ? s.labelAr : s.labelEn}
                </option>
              ))}
            </select>
          </label>
        )}
        {isMulti && (
          <p className="iw-hint iw-field--full">
            {t(
              'للمشروع/الإدخال الجماعي: حدّد أعداد المباعة والمؤجرة والمحجوزة في خطوة الترقيم. الباقي يُسجَّل كمتاح.',
              'For project/bulk: set sold, rented, and reserved counts on the numbering step. The rest stay available.'
            )}
          </p>
        )}
        {(form.availableFor === 'rent' || form.availableFor === 'both') && (
          <label className="iw-field">
            <span>{t('الإيجار الشهري (ر.س)', 'Monthly rent (SAR)')}</span>
            <input type="number" min="0" value={form.monthlyRent} onChange={(e) => patch({ monthlyRent: e.target.value })} />
          </label>
        )}
        {(form.availableFor === 'sale' || form.availableFor === 'both') && (
          <label className="iw-field">
            <span>{t('سعر البيع (ر.س)', 'Sale price (SAR)')}</span>
            <input type="number" min="0" value={form.salePrice} onChange={(e) => patch({ salePrice: e.target.value })} />
          </label>
        )}

        {groups.map((group) => {
          const numbers = group.fields.filter((f) => f.type !== 'boolean');
          const toggles = group.fields.filter((f) => f.type === 'boolean');
          return (
            <div key={group.id} className="iw-spec-group iw-field--full">
              <div className="iw-amenities-label">{isAr ? group.labelAr : group.labelEn}</div>
              {numbers.length > 0 && (
                <div className="iw-form-grid iw-form-grid--tight">{numbers.map(renderField)}</div>
              )}
              {toggles.length > 0 && (
                <div className="iw-chip-row">{toggles.map(renderField)}</div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderBulkStep = () => (
    <div className="iw-form-grid">
      <label className="iw-field">
        <span>{t('عدد الوحدات', 'Number of units')} *</span>
        <input type="number" min="1" max="500" value={form.bulkCount} onChange={(e) => patch({ bulkCount: e.target.value })} />
      </label>
      <label className="iw-field">
        <span>{t('بادئة الترقيم', 'Number prefix')}</span>
        <input value={form.bulkPrefix} onChange={(e) => patch({ bulkPrefix: e.target.value })} placeholder={t('مثال: A', 'e.g. A')} />
      </label>
      <label className="iw-field">
        <span>{t('يبدأ من', 'Start at')}</span>
        <input type="number" min="1" value={form.bulkStart} onChange={(e) => patch({ bulkStart: e.target.value })} />
      </label>
      <label className="iw-field">
        <span>{t('أصفار بادئة (اختياري)', 'Zero padding')}</span>
        <input type="number" min="0" max="4" value={form.bulkPad} onChange={(e) => patch({ bulkPad: e.target.value })} />
      </label>

      <div className="iw-status-counts iw-field--full">
        <strong>{t('حالة الوحدات عند الإدخال', 'Unit statuses on create')}</strong>
        <p className="iw-field-hint">
          {t(
            'النسبة المباعة = عدد المباعة ÷ الإجمالي. لا تُدخل نسبة واحدةحدة للمشروع.',
            'Sold % = sold count ÷ total. Do not set one status for the whole project.'
          )}
        </p>
        <div className="iw-form-grid iw-form-grid--tight">
          <label className="iw-field">
            <span>{t('مباعة', 'Sold')}</span>
            <input type="number" min="0" value={form.bulkSold} onChange={(e) => patch({ bulkSold: e.target.value })} />
          </label>
          <label className="iw-field">
            <span>{t('مؤجرة', 'Rented')}</span>
            <input type="number" min="0" value={form.bulkRented} onChange={(e) => patch({ bulkRented: e.target.value })} />
          </label>
          <label className="iw-field">
            <span>{t('محجوزة (قيد الإتمام)', 'Reserved (in progress)')}</span>
            <input type="number" min="0" value={form.bulkReserved} onChange={(e) => patch({ bulkReserved: e.target.value })} />
          </label>
          <label className="iw-field">
            <span>{t('متاحة (الباقي)', 'Available (remainder)')}</span>
            <input type="number" value={bulkStatusPreview?.vacant ?? 0} readOnly />
          </label>
        </div>
        {bulkStatusPreview && (
          <p className="iw-preview-line">
            {t('معاينة', 'Preview')}: {bulkStatusPreview.vacant} {t('متاحة', 'available')} · {bulkStatusPreview.reserved} {t('محجوزة', 'reserved')} · {bulkStatusPreview.sold} {t('مباعة', 'sold')} · {bulkStatusPreview.rented} {t('مؤجرة', 'rented')}
            {bulkStatusPreview.count > 0
              ? ` · ${Math.round((bulkStatusPreview.sold / bulkStatusPreview.count) * 100)}% ${t('مباعة', 'sold')}`
              : ''}
          </p>
        )}
      </div>

      <div className="iw-preview iw-field--full">
        <strong>{t('معاينة الترقيم', 'Numbering preview')}</strong>
        <p>{unitPreview.slice(0, 8).join(' · ')}{unitPreview.length > 8 ? ` … (+${unitPreview.length - 8})` : ''}</p>
      </div>
    </div>
  );

  const renderMarketingStep = () => {
    const canProject = form.mode === 'project' || form.projectName || form.useExistingProject;
    const options = [
      {
        id: 'later',
        labelAr: 'لاحقاً',
        labelEn: 'Later',
        descAr: 'أنشئ المخزون الآن بدون حملة تسويق',
        descEn: 'Create inventory now without a marketing campaign',
      },
    ];
    if (canProject) {
      options.push(
        {
          id: 'project',
          labelAr: 'تسويق المشروع ككل',
          labelEn: 'Market whole project',
          descAr: 'حملة واحدةحدة للمشروع — ليست ترخيص إعلان واحد لكل الوحدات',
          descEn: 'One project campaign — not one REGA license for every unit',
        },
        {
          id: 'both',
          labelAr: 'تسويق مشروع + وحدات',
          labelEn: 'Project promo + unit campaigns',
          descAr: 'حملة للمشروع ومتابعة تسويق لكل وحدة',
          descEn: 'Project promo plus a campaign per unit',
        },
      );
    }
    options.push({
      id: 'units',
      labelAr: 'تسويق كل وحدة',
      labelEn: 'Market each unit',
      descAr: 'المعتاد للوساطة — ترخيص الإعلان يُسجَّل لاحقاً لكل وحدة عند النشر',
      descEn: 'Usual brokerage path — REGA ad license is recorded later per unit when posting',
    });

    return (
      <div className="iw-cards iw-cards--stack">
        <p className="iw-hint">
          {t(
            'هذا نطاق التسويق داخل ريل فقط. رقم ترخيص الإعلان (هيئة العقار) ورقم الإعلان على المنصة يُدخلان عند إنشاء/نشر الإعلان.',
            'This is marketing scope inside Real only. REGA ad license # and the platform ad # are entered when creating/posting the listing.'
          )}
        </p>
        {options.map((o) => (
          <button
            key={o.id}
            type="button"
            className={`iw-card iw-card--row${form.marketing === o.id ? ' iw-card--active' : ''}`}
            onClick={() => patch({ marketing: o.id })}
          >
            <span className="iw-card__title">{isAr ? o.labelAr : o.labelEn}</span>
            <span className="iw-card__desc">{isAr ? o.descAr : o.descEn}</span>
          </button>
        ))}
        {form.availableFor === 'both' && form.marketing !== 'later' && (
          <label className="iw-field iw-field--full">
            <span>{t('نوع الحملة الافتراضي', 'Default campaign type')}</span>
            <select value={form.listingType} onChange={(e) => patch({ listingType: e.target.value })}>
              <option value="rent">{t('إيجار', 'Rent')}</option>
              <option value="sale">{t('بيع', 'Sale')}</option>
            </select>
          </label>
        )}
      </div>
    );
  };

  const marketingLabel = ({
    later: t('لاحقاً', 'Later'),
    project: t('تسويق المشروع ككل', 'Market whole project'),
    both: t('تسويق مشروع + وحدات', 'Project promo + unit campaigns'),
    units: t('تسويق كل وحدة', 'Market each unit'),
  })[form.marketing] || form.marketing;

  const statusReviewLabel = () => {
    if (isMulti && bulkStatusPreview) {
      return `${bulkStatusPreview.vacant} ${t('متاحة', 'available')} · ${bulkStatusPreview.sold} ${t('مباعة', 'sold')} · ${bulkStatusPreview.rented} ${t('مؤجرة', 'rented')} · ${bulkStatusPreview.reserved} ${t('محجوزة', 'reserved')}`;
    }
    const s = UNIT_STATUS_OPTIONS.find((x) => x.value === form.status);
    return s ? (isAr ? s.labelAr : s.labelEn) : form.status;
  };

  const renderReviewStep = () => (
    <div>
      <img className="iw-cover-preview iw-cover-preview--review" src={coverPreview} alt="" />
      <dl className="iw-review">
        <div><dt>{t('النوع', 'Mode')}</dt><dd>{WIZARD_MODES.find((m) => m.id === form.mode)?.[isAr ? 'labelAr' : 'labelEn']}</dd></div>
        <div><dt>{t('المدينة', 'City')}</dt><dd>{form.city}</dd></div>
        <div><dt>{t('الحي', 'Neighborhood')}</dt><dd>{resolvedDistrict(form)}</dd></div>
        {form.projectName && <div><dt>{t('المشروع', 'Project')}</dt><dd>{form.projectName}</dd></div>}
        <div><dt>{t('نوع الوحدة', 'Unit type')}</dt><dd>{getUnitTypeLabel(form.unitType, language)}</dd></div>
        <div><dt>{t('عدد الوحدات', 'Units')}</dt><dd>{unitPreview.length}</dd></div>
        <div><dt>{t('الحالة', 'Status')}</dt><dd>{statusReviewLabel()}</dd></div>
        <div><dt>{t('نطاق التسويق', 'Marketing scope')}</dt><dd>{marketingLabel}</dd></div>
        <div className="iw-review__nums"><dt>{t('الترقيم', 'Numbers')}</dt><dd>{unitPreview.slice(0, 12).join(', ')}</dd></div>
      </dl>
    </div>
  );

  const stepTitle = {
    mode: t('ماذا تريد إضافته؟', 'What are you adding?'),
    location: t('الموقع والهيكل', 'Location & structure'),
    type: t('نوع الوحدة والصورة', 'Unit type & photo'),
    specs: t('المواصفات والغرض', 'Specs & purpose'),
    bulk: t('الترقيم والحالات', 'Numbering & statuses'),
    marketing: t('نطاق التسويق', 'Marketing scope'),
    review: t('مراجعة وحفظ', 'Review & save'),
  };

  const renderStep = () => {
    switch (currentStep) {
      case 'mode': return renderModeStep();
      case 'location': return renderLocationStep();
      case 'type': return renderTypeStep();
      case 'specs': return renderSpecsStep();
      case 'bulk': return renderBulkStep();
      case 'marketing': return renderMarketingStep();
      case 'review': return renderReviewStep();
      default: return null;
    }
  };

  return (
    <div className="iw-overlay" role="dialog" aria-modal="true">
      <div className="iw-modal" dir={isAr ? 'rtl' : 'ltr'}>
        <header className="iw-header">
          <div>
            <h2>{t('إضافة للمخزون العقاري', 'Add to inventory')}</h2>
            <p className="iw-step-label">
              {t('الخطوة', 'Step')} {stepIndex + 1}/{steps.length} — {stepTitle[currentStep]}
            </p>
          </div>
          <button type="button" className="iw-close" onClick={onClose} aria-label={t('إغلاق', 'Close')}>
            <FaTimes />
          </button>
        </header>

        <div className="iw-progress">
          {steps.map((s, i) => (
            <div key={s} className={`iw-progress__dot${i <= stepIndex ? ' iw-progress__dot--on' : ''}`} />
          ))}
        </div>

        <div className="iw-body">{renderStep()}</div>

        {error && <div className="iw-error">{error}</div>}

        <footer className="iw-footer">
          <button type="button" className="iw-btn iw-btn--ghost" onClick={stepIndex === 0 ? onClose : goBack}>
            {stepIndex === 0 ? t('إلغاء', 'Cancel') : (
              <><FaChevronLeft /> {t('رجوع', 'Back')}</>
            )}
          </button>
          {currentStep === 'review' ? (
            <button type="button" className="iw-btn iw-btn--primary" disabled={submitting} onClick={handleSubmit}>
              <FaCheck /> {submitting ? t('جارٍ الحفظ…', 'Saving…') : t('حفظ', 'Save')}
            </button>
          ) : (
            <button type="button" className="iw-btn iw-btn--primary" onClick={goNext}>
              {t('التالي', 'Next')} <FaChevronRight />
            </button>
          )}
        </footer>
      </div>
    </div>
  );
}
